import React from 'react';
import { SequenceEvent } from '@/services/api';
import { SVG_COLORS, getSvgColor, toSvgX, toSvgY } from './helpers';

const BuildUpPitch: React.FC<{ sequence: SequenceEvent[] }> = ({ sequence }) => {
  // Filter to events that have location data
  const locatedEvents = sequence.filter(e => e.location);
  if (locatedEvents.length === 0) return null;

  // For each event, determine the end point for a connection line:
  // - If the event has an endLocation (pass, carry), draw to that
  // - Otherwise draw to the next event's location
  const connections: Array<{
    x1: number; y1: number; x2: number; y2: number;
    color: string; dashed: boolean;
  }> = [];

  for (let i = 0; i < locatedEvents.length; i++) {
    const ev = locatedEvents[i];
    const loc = ev.location!;
    const sx = toSvgX(loc.x);
    const sy = toSvgY(loc.y);

    let ex: number | null = null;
    let ey: number | null = null;

    if (ev.endLocation) {
      ex = toSvgX(ev.endLocation.x);
      ey = toSvgY(ev.endLocation.y);
    } else if (i < locatedEvents.length - 1) {
      const nextLoc = locatedEvents[i + 1].location!;
      ex = toSvgX(nextLoc.x);
      ey = toSvgY(nextLoc.y);
    }

    if (ex !== null && ey !== null) {
      // Skip zero-length lines
      const dist = Math.sqrt((ex - sx) ** 2 + (ey - sy) ** 2);
      if (dist > 0.5) {
        connections.push({
          x1: sx, y1: sy, x2: ex, y2: ey,
          color: getSvgColor(ev.action, ev.actionDetail),
          dashed: ev.accurate === false,
        });
      }
    }
  }

  return (
    <div className="w-full">
      <svg viewBox="-1 -1 102 67" className="w-full rounded border border-gray-300 dark:border-gray-600 bg-green-900/10 dark:bg-green-900/20">
        {/* Arrow marker definitions */}
        <defs>
          {Object.entries(SVG_COLORS).map(([key, color]) => (
            <marker
              key={key}
              id={`arrow-${key}`}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="4"
              markerHeight="4"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
          ))}
        </defs>

        {/* Pitch markings */}
        <rect x="0" y="0" width="100" height="65" fill="none" stroke="#4a7c4f" strokeWidth="0.5" />
        <line x1="50" y1="0" x2="50" y2="65" stroke="#4a7c4f" strokeWidth="0.3" />
        <circle cx="50" cy="32.5" r="9" fill="none" stroke="#4a7c4f" strokeWidth="0.3" />
        <circle cx="50" cy="32.5" r="0.5" fill="#4a7c4f" />
        {/* Left penalty area */}
        <rect x="0" y="13" width="17" height="39" fill="none" stroke="#4a7c4f" strokeWidth="0.3" />
        <rect x="0" y="22" width="6" height="21" fill="none" stroke="#4a7c4f" strokeWidth="0.3" />
        <circle cx="11" cy="32.5" r="0.5" fill="#4a7c4f" />
        {/* Right penalty area */}
        <rect x="83" y="13" width="17" height="39" fill="none" stroke="#4a7c4f" strokeWidth="0.3" />
        <rect x="94" y="22" width="6" height="21" fill="none" stroke="#4a7c4f" strokeWidth="0.3" />
        <circle cx="89" cy="32.5" r="0.5" fill="#4a7c4f" />

        {/* Connection lines with arrows */}
        {connections.map((conn) => {
          // Shorten line slightly so arrow doesn't overlap the dot
          const dx = conn.x2 - conn.x1;
          const dy = conn.y2 - conn.y1;
          const len = Math.sqrt(dx * dx + dy * dy);
          const shortenBy = 2;
          const ratio = Math.max(0, (len - shortenBy) / len);
          const endX = conn.x1 + dx * ratio;
          const endY = conn.y1 + dy * ratio;

          // Find the matching marker key
          const markerKey = Object.keys(SVG_COLORS).find(k => SVG_COLORS[k] === conn.color) || 'default';

          return (
            <line
              key={`conn-${conn.x1}-${conn.y1}-${conn.x2}-${conn.y2}`}
              x1={conn.x1}
              y1={conn.y1}
              x2={endX}
              y2={endY}
              stroke={conn.color}
              strokeWidth="0.6"
              strokeDasharray={conn.dashed ? '2 1' : undefined}
              opacity={0.7}
              markerEnd={`url(#arrow-${markerKey})`}
            />
          );
        })}

        {/* Event dots */}
        {locatedEvents.map((ev) => {
          const sx = toSvgX(ev.location!.x);
          const sy = toSvgY(ev.location!.y);
          const isGoal = ev.actionDetail === 'goal';
          const isFoul = ev.action === 'foul' || ev.action === 'infraction';
          const color = getSvgColor(ev.action, ev.actionDetail);
          const seqNum = sequence.indexOf(ev) + 1;

          return (
            <g key={`dot-${ev.minute}-${ev.second}-${ev.action}`}>
              {isGoal ? (
                <>
                  {/* Goal: outer ring + filled center */}
                  <circle cx={sx} cy={sy} r="3" fill="white" stroke="#22c55e" strokeWidth="0.8" />
                  <circle cx={sx} cy={sy} r="2" fill="#22c55e" />
                  <text
                    x={sx}
                    y={sy + 0.8}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="2.2"
                    fontWeight="bold"
                    fill="white"
                  >
                    G
                  </text>
                </>
              ) : isFoul ? (
                <>
                  {/* Foul: red outer ring + filled center with F */}
                  <circle cx={sx} cy={sy} r="3" fill="white" stroke="#ef4444" strokeWidth="0.8" />
                  <circle cx={sx} cy={sy} r="2" fill="#ef4444" />
                  <text
                    x={sx}
                    y={sy + 0.8}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="2.2"
                    fontWeight="bold"
                    fill="white"
                  >
                    F
                  </text>
                </>
              ) : (
                <>
                  <circle cx={sx} cy={sy} r="1.8" fill={color} stroke="white" strokeWidth="0.4" opacity={0.9} />
                  <text
                    x={sx}
                    y={sy + 0.6}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="1.8"
                    fontWeight="bold"
                    fill="white"
                  >
                    {seqNum}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* Scorer label near goal dot */}
        {locatedEvents
          .filter(ev => ev.actionDetail === 'goal' && ev.player)
          .map((ev) => {
            const sx = toSvgX(ev.location!.x);
            const sy = toSvgY(ev.location!.y);
            // Position label above the goal dot, clamping to stay in view
            const labelY = sy > 8 ? sy - 5 : sy + 6;
            return (
              <text
                key={`scorer-${ev.minute}-${ev.player!.name}`}
                x={sx}
                y={labelY}
                textAnchor="middle"
                fontSize="2.5"
                fontWeight="bold"
                fill="#22c55e"
                className="drop-shadow-sm"
              >
                {ev.player!.name.split(' ').pop()}
              </text>
            );
          })}

        {/* Foul label near foul dot */}
        {locatedEvents
          .filter(ev => (ev.action === 'foul' || ev.action === 'infraction') && ev.player)
          .map((ev, i) => {
            const sx = toSvgX(ev.location!.x);
            const sy = toSvgY(ev.location!.y);
            const labelY = sy > 8 ? sy - 5 : sy + 6;
            return (
              <text
                key={`foul-label-${i}`}
                x={sx}
                y={labelY}
                textAnchor="middle"
                fontSize="2.2"
                fontWeight="bold"
                fill="#ef4444"
                className="drop-shadow-sm"
              >
                Foul on {ev.player!.name.split(' ').pop()}
              </text>
            );
          })}
      </svg>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 px-1">
        {[
          { label: 'Pass', color: SVG_COLORS.pass },
          { label: 'Carry', color: SVG_COLORS.carry },
          { label: 'Shot/Goal', color: SVG_COLORS.shot },
          { label: 'Duel', color: SVG_COLORS.duel },
          { label: 'Foul', color: SVG_COLORS.foul },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BuildUpPitch;
